/**
 * The filter mask — **client round 4 E4**, 2026-08-23, extended to the country rung the same
 * day (round 5, "Two geographies" below).
 *
 * > *"When a cascade filter is active and a raster layer is on, the raster is masked to the
 * > scoped unit's boundary and the map zooms to it."*
 *
 * The §F5.8 filter already narrows everything that is *drawn from the database*: the
 * choropleth asks §D3 for the subtree, the boundary rows drop to the scope's own outline and
 * its children's, the legend reclassifies over what is left. A raster does none of that — it
 * is imagery, served in whole tiles by TiTiler or a WMS upstream, and it kept covering the
 * Sahel while everything around it had shrunk to one region. So the raster is cut to the same
 * outline with the mechanism §G6 already provides for exactly this: a `clip-path` on
 * `rasterPane`, re-projected as the view moves.
 *
 * ## What it cuts, and what it does not
 *
 * **`rasterPane` only.** §G6's AOI clip masks `rasterPane` *and* `choroPane`, because a
 * hand-drawn AOI is a question about a piece of ground and both thematic layers have to
 * answer it. This mask has nothing to say to the choropleth: that pane is already exactly the
 * scope's subtree, drawn polygon by polygon from `?parent=` (`Choropleth.tsx`), so clipping it
 * would be cutting a shape out of itself — and would shave the boundary strokes of the very
 * units the filter selected. The basemap and the §F5.8 boundaries are untouched for §G6's own
 * reason: the reader has to be able to see where the filtered unit sits.
 *
 * **Only while a raster is actually drawn.** No raster on, no mask: the pane is empty, and a
 * rule with no subject would still take swipe away from the reader (see the precedence below).
 *
 * ## Precedence, and why it lives in the store
 *
 * One element has one `clip-path`, and three features now want `rasterPane`'s. §G6 settled
 * the first pair in `lib/state.ts` (`setAoiClip`/`setSwipe` are mutually exclusive), and E4
 * settles the rest in the same place, as `scopeMaskActive`:
 *
 * > **AOI clip > scope mask > swipe.**
 *
 * *Above the mask:* a reader who draws an AOI and presses **Clip** is asking for this exact
 * operation with a polygon of their own. If the mask won, that button would do nothing
 * visible under a filter and would read as broken. So `scopeMaskActive` is false while
 * `aoiClip` is on, this hook stands down, and releasing the clip brings the mask back with no
 * further state — both are derived from the same store, so there is nothing to remember.
 *
 * *Below the mask:* swipe writes `inset(0 0 0 Xpx)` on the same two panes, and the two cannot
 * share. It is refused while the mask holds (`setSwipe`, with `MapTools` saying why), and any
 * write that raises the mask releases it (`releasingSwipe`). That is the treatment the AOI
 * clip already gives swipe, which is what E4 asks for: the mask is the filter's, and the
 * filter outranks a comparison tool.
 *
 * ## Two geographies, because the cascade has two ways of being set
 *
 * **Round 5.** The cascade's first rung is its country box, and picking in it writes the §F1
 * `country` field and **no scope** — there is no `Scope` for a country, because §D3's filter
 * is a *parent* id and a country's children are what the map draws anyway. So the map was
 * filtered to Burkina Faso by every other reading (the choropleth is one country's regions,
 * *Clear filter* offers to clear it) while this mask, which asked only for `scope`, stood
 * down and left `soc` painted across all four countries. That is E4's own bug, one rung up.
 *
 * `maskSource` is the whole of the fix: **scope, else country, else nothing**. The order is
 * the cascade's, and the deeper pick wins for the obvious reason — a region scope inside
 * Burkina is a narrower answer to the same question, and it is the one the reader made last.
 *
 * ## Geometry
 *
 * For a scope, the outline is the unit's own polygon, from the §D2 query the map is **already
 * making**: `?parent=<scope>` composed with the scope's own type and level returns exactly
 * that one feature (`?parent=` is inclusive — see `lib/api.ts::fetchUnits`), and that is the
 * same query key `HierarchyLayers` uses for the scope's own boundary row, so under a filter
 * this mask is a cache read and not a request. `z7`, the row's own simplification: the mask is
 * a silhouette a few hundred pixels wide and the full geometry would be 14 MB of coordinates
 * to draw the same edge.
 *
 * For a country it is the ADM0 outline out of `(admin, 0, ALL, z7)` — the four-feature payload
 * `GeographyFocus` holds for the whole life of the map (it is what zooms the view to the
 * country, which round 5 leaves exactly as it was) and the "Countries" boundary row draws
 * from. Same key, so this is a cache read too, and the feature is picked by
 * `properties.country` because that is the field `GeographyFocus` matches on.
 *
 * Layer points and `path()`, both for `ProgrammeClip`'s reasons (see its header): a pane's
 * children are positioned in layer-point space, so a mask expressed there is glued to the
 * ground rather than re-glued on every `move`, and `path()` — unlike CSS `polygon()` — takes
 * more than one ring, which is how a unit with an island keeps it. That last point is not a
 * corner case at ADM0: a country's simplified outline routinely carries islands and slivers
 * as separate parts, and `outerRingsOf` returns one ring per part for a MultiPolygon exactly
 * as it does under a scope — one polygon's worth of mask would have cut the rest of the
 * country's raster away.
 */
import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type L from 'leaflet';

import { fetchUnits, queryKeys, type UnitsFC } from '../../lib/api';
import { outerRingsOf } from '../../lib/geo';
import { useAppStore, type AppState, type Country, type Scope } from '../../lib/state';
import { ringsClipPath, type LonLatRing } from './ProgrammeClip';
import { CLIP_PANES } from './AoiClip';

/** §G6 gives the AOI clip both thematic panes; this one takes the raster half. */
export const SCOPE_CLIP_PANE = CLIP_PANES[0];

/** Write (or release, with `''`) the raster pane's clip. */
export function setScopeClip(map: L.Map, value: string): void {
  const pane = map.getPane(SCOPE_CLIP_PANE);
  if (pane) pane.style.clipPath = value;
}

/**
 * The scope unit's outer ring(s), out of the FeatureCollection its row was fetched with.
 *
 * The feature is looked up **by id** rather than taken as `features[0]`: for a region scope
 * the row is `(admin, 1, parent=BF50)`, which is that region alone today, but the shape of
 * the answer is the server's to change and a mask cut to the wrong unit is worse than no mask
 * at all. No match, or a unit whose simplified geometry collapsed to nothing, is an empty
 * list — which `ringsClipPath` turns into `''`, i.e. no mask, rather than into an empty
 * polygon that would hide the raster completely.
 */
export function scopeRings(fc: UnitsFC | undefined, unitId: string): LonLatRing[] {
  const feature = fc?.features.find((f) => f.properties.id === unitId);
  return feature ? (outerRingsOf(feature.geometry) as LonLatRing[]) : [];
}

/**
 * The country's ADM0 outer ring(s), out of the four-feature level-0 payload.
 *
 * By `properties.country`, which is the field `GeographyFocus` picks the same feature with to
 * zoom the view: one lookup rule for the two halves of "the map is on this country". An
 * ADM0 that has not landed, or one whose simplified geometry collapsed, is an empty list —
 * i.e. no mask, exactly as `scopeRings` handles the same case.
 */
export function countryRings(fc: UnitsFC | undefined, country: string): LonLatRing[] {
  const feature = fc?.features.find((f) => f.properties.country === country);
  return feature ? (outerRingsOf(feature.geometry) as LonLatRing[]) : [];
}

/** What the mask is cut to, if anything — see the header's "two geographies". */
export type MaskSource =
  | { kind: 'scope'; scope: Scope }
  | { kind: 'country'; country: Exclude<Country, 'ALL'> }
  | null;

/**
 * E4's whole rule, as one pure function: **what is the raster masked to right now?**
 *
 * The two conditions that are not geography come first and are the ones documented in
 * `lib/state.ts::scopeMaskActive` — the hand-drawn AOI clip outranks this mask, and an empty
 * raster pane is not worth masking. Then the cascade's own order: the scope if there is one,
 * else the country rung, else nothing.
 *
 * `rasterDrawn` is the caller's fact rather than the store's (`RasterLayers` holds the §D5
 * catalogue), which is why this takes it as an argument instead of reading `layersOn`.
 */
export function maskSource(
  s: Pick<AppState, 'scope' | 'country' | 'aoiClip'>,
  rasterDrawn: boolean,
): MaskSource {
  if (s.aoiClip || !rasterDrawn) return null;
  if (s.scope) return { kind: 'scope', scope: s.scope };
  return s.country === 'ALL' ? null : { kind: 'country', country: s.country };
}

/** The §D2 row that holds one scope unit's own geometry — `type`+`level` inside `?parent=`. */
function scopeUnitQuery(scope: Scope) {
  return {
    type: scope.unitType,
    level: scope.unitType === 'admin' ? (scope.adminLevel ?? 1) : null,
  } as const;
}

/**
 * Keep `rasterPane` masked to the §F5.8 filter's geography while E4's conditions hold.
 *
 * **Called from `RasterLayers`**, which is the component that already knows the raster
 * condition: it holds the §D5 catalogue and renders one tile layer per drawn raster, so
 * `rasterDrawn` is a fact it has rather than one this hook would have to re-derive. The
 * alternative — reading the store's raster register (`selectActiveRaster`) from the tool
 * column — cannot be woken up: `registerRasterLayers` writes no state when the set already
 * satisfies R4, so a `ly=soc` permalink restored before `/layers` landed would leave the mask
 * believing no raster was on with nothing to tell it otherwise.
 *
 * Its position in the tree relative to `useAoiClip` (in `MapTools`) is not load-bearing:
 * React runs every destroy in a commit before any create, so the hand-off between the two
 * writers of this pane's `clip-path` cannot leave a stale mask either way round.
 */
export function useScopeClip(map: L.Map, rasterDrawn: boolean): void {
  const scope = useAppStore((s) => s.scope);
  const aoiClip = useAppStore((s) => s.aoiClip);
  // The §F1 global selector, not `scope.country`: it is what `HierarchyLayers` keys its
  // boundary queries with, and the two must land on one cache entry rather than on two
  // identical requests. `setScope` keeps it equal to the scope's country anyway — and with
  // no scope it is the filter itself (round 5, `maskSource`).
  const country = useAppStore((s) => s.country);

  // Memoised so the object is stable while the three fields are: it is an effect dependency
  // below, and a fresh `{kind:'country'}` on every render would re-write the clip on every
  // unrelated render of `RasterLayers` (an opacity drag, a tile load).
  const source = useMemo(
    () => maskSource({ scope, country, aoiClip }, rasterDrawn),
    [scope, country, aoiClip, rasterDrawn],
  );

  const unit = scope ? scopeUnitQuery(scope) : null;
  const type = unit?.type ?? 'admin';
  const level = unit?.level ?? null;
  const parent = scope?.unitId ?? null;

  const { data: scopeUnits } = useQuery({
    queryKey: queryKeys.units(type, level, country, 'z7', parent),
    queryFn: () => fetchUnits(type, level, country, 'z7', parent),
    enabled: source?.kind === 'scope',
    staleTime: 30 * 60 * 1000,
  });

  // The level-0 payload, on `GeographyFocus`'s exact key — four ADM0 features it has already
  // fetched to zoom the view to this same country, so the country mask costs no request
  // either. Both hooks are declared unconditionally and gated by `enabled`, which is what
  // keeps the hook order static while only one of them is ever the live one.
  const { data: admin0 } = useQuery({
    queryKey: queryKeys.units('admin', 0, 'ALL', 'z7'),
    queryFn: () => fetchUnits('admin', 0, 'ALL', 'z7'),
    enabled: source?.kind === 'country',
    staleTime: 30 * 60 * 1000,
  });

  const rings = useMemo(() => {
    if (!source) return [];
    return source.kind === 'scope'
      ? scopeRings(scopeUnits, source.scope.unitId)
      : countryRings(admin0, source.country);
  }, [source, scopeUnits, admin0]);

  useEffect(() => {
    if (!source || !rings.length) return;
    const apply = () => setScopeClip(map, ringsClipPath(map, rings));
    apply();
    // The same four events `useAoiClip` and `useProgrammeClip` listen for, for the same
    // reasons: `zoom`/`viewreset` move the pixel origin the layer points are measured from,
    // and `resize` fires when `invalidateSize` runs (collapsing the sidebar is 332 px).
    map.on('move zoom viewreset resize', apply);
    return () => {
      map.off('move zoom viewreset resize', apply);
      setScopeClip(map, '');
    };
  }, [map, source, rings]);
}
